import { useEffect, useId } from 'react';
import type React from 'react';

import { createPortal } from 'react-dom';

import { Button, Image, Text } from '@change/ds-core-ui';
import { CheckIcon, EmailIcon, GroupIcon, SendIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { Translate, TranslatePlural, useI18n } from '@change-corgi/core-react/i18n';
import { ClientRender } from '@change-corgi/core-react/ssr/render';

import megaphone from '#src/assets/megaphone-outreach.png';

import { useDecisionMakerOutreachStats } from '#src/app/shared/hooks/contactDecisionMaker';
import { useInertPageRoot } from '#src/app/shared/hooks/focus';
import { focus } from '#src/app/shared/utils/dom';

import type { ContactDecisionMakerSuccessStageProps } from '../types';

import { useCountUp } from './hooks/useCountUp';

/**
 * Screen 4 (CHANGE-97533). The confirmation after a message is accepted for delivery.
 *
 * Not a DS sheet. Below `md` it is a full-bleed overlay, so the petition behind it is gone and this
 * is the whole screen; from `md` up the same element is the backdrop and an inner panel is the
 * centred dialog. The share sheet layers on top of it rather than replacing it.
 *
 * It is still a dialog, and has to say so. `fixed inset-0` hides the petition visually but leaves it
 * reachable, so without `aria-modal` a screen reader reads straight through to the page underneath
 * and focus starts on `<body>`.
 *
 * `aria-modal` alone would keep assistive technology out of the background while leaving a sighted
 * keyboard user free to tab into it — and because completion is recorded the moment the send
 * succeeds, the impact section's "Invite others" button is already mounted behind this overlay.
 *
 * So it is portaled to `document.body` and the page root is marked `inert` while it is up. Trapping
 * focus locally was tried and rejected: it fights the share sheet's own trap when that layers on top.
 * `inert` has no such conflict — the share sheet portals out of the root as well, so it stays
 * reachable.
 */
export const Success: React.FC<ContactDecisionMakerSuccessStageProps> = ({
	decisionMaker,
	petitionId,
	flow,
	headingRef,
	isShareLoading,
}) => {
	const { translate, localizeNumber } = useI18n();
	const { contactedSupporterCount: reportedCount } = useDecisionMakerOutreachStats(petitionId);
	// The viewer just sent a message, so the total is at least one whatever the query says. The stub
	// resolver returns 0 today, and en-US CLDR has no `zero` form, so 0 selects `other` and the
	// sub-line reads "You're one of 0 supporters who've reached out" on every success.
	const contactedSupporterCount = reportedCount === undefined ? undefined : Math.max(1, reportedCount);
	const displayedCount = useCountUp(contactedSupporterCount);
	// Everyone but the viewer. The "joins N others" row only earns its place once there is at least
	// one other story to join — "joins 0 others" advertises the absence.
	const otherSupporterCount = contactedSupporterCount === undefined ? undefined : contactedSupporterCount - 1;
	const headingId = useId();

	// Portaled out of the page root below, so this is what keeps the background out of the tab order.
	useInertPageRoot(true);

	// The overlay is `fixed` and rendered in place, so when its own content fits there is nothing to
	// scroll inside it and wheel/touch scroll reaches the petition behind the opaque overlay — the
	// page moves under the overlay and "Back to the petition" lands somewhere else. Locking the body
	// is the whole fix and needs nothing from the DS. The previous value is restored rather than
	// cleared: the share sheet layers over this screen and sets its own lock.
	useEffect(() => {
		const { body } = document;
		const previousOverflow = body.style.overflow;
		body.style.overflow = 'hidden';

		return () => {
			body.style.overflow = previousOverflow;
		};
	}, []);

	// The overlay replaces the page, and nothing here is auto-focused, so focus would sit on `<body>`
	// behind it. Moved to the heading rather than a button, so the first thing announced is what
	// happened. Focus returns to the entry control when the flow closes — the flow owns that.
	useEffect(() => {
		focus(headingRef.current, { makeFocusable: true });
	}, [headingRef]);

	return createPortal(
		// `open`, not `showModal()`: a modal dialog goes into the browser's top layer, which no z-index
		// can beat, and the share sheet has to render above this. `aria-modal` still keeps assistive
		// technology out of the background, which matches what the opaque overlay does visually.
		// `m-0 h-full max-h-none max-w-none` undoes the UA dialog box (`inset-0` alone leaves its
		// `height: fit-content`); the rest is the overlay.
		<dialog
			open
			aria-modal="true"
			aria-labelledby={headingId}
			// Below `md` the dialog IS the screen. From `md` up it is the backdrop (the DS overlay's own
			// classes) and the panel below is the dialog, so on desktop it reads like every other sheet in
			// the flow instead of replacing the page. `my-auto` on the panel, not `justify-center` here:
			// that keeps a panel taller than the viewport scrollable instead of clipping its top.
			className="bg-background-base md:bg-fill-overlay fixed inset-0 z-70 m-0 flex h-full max-h-none w-full max-w-none flex-col items-center overflow-y-auto overscroll-contain text-center md:p-6 md:backdrop-blur-[2px]"
			data-testid="contact-decision-maker-success"
		>
			<div className="md:bg-background-base md:elevation-floating flex min-h-full w-full flex-col items-center px-7 pt-12 pb-6 md:my-auto md:min-h-0 md:max-w-[480px] md:rounded-xl md:p-8">
				{/* The design's megaphone (`assets/megaphone-sm.png`, 400px for a 200px box). Decorative, so
				    `alt=""`; the heading carries the message. `aspect-auto` undoes the DS Image's 16:9 default,
				    which would stretch it sideways. */}
				<Image src={megaphone} alt="" className="aspect-auto h-[200px] w-auto" />

				<Text
					as="h1"
					id={headingId}
					ref={headingRef}
					tabIndex={-1}
					className="text-size-heading-small text-typography-strong mt-6 max-w-[290px]"
				>
					<Translate value="corgi.contact_decision_maker.success.heading" />
				</Text>

				{/* Hidden until the count resolves rather than rendering a placeholder number: the sub-line's
				    whole job is to say how many people have done this, and a wrong figure undercuts it. */}
				{contactedSupporterCount !== undefined && (
					<Text className="text-size-body text-typography-weak mt-3 max-w-[300px]">
						{/* Intl output must not differ between SSR and hydration. */}
						<ClientRender>
							{/* `count` is the real total, not the animated value: pluralisation must not flip
							    mid-animation, which flashed the singular line for one tick on every total above one. */}
							<TranslatePlural
								value="corgi.contact_decision_maker.success.subline"
								count={contactedSupporterCount}
								replacements={{ supporterCount: localizeNumber(displayedCount ?? contactedSupporterCount) }}
							/>
						</ClientRender>
					</Text>
				)}

				<PromiseCard decisionMakerName={decisionMaker.displayName} otherSupporterCount={otherSupporterCount} />

				{/* Text link above the primary, as the design draws every two-CTA footer (CHANGE-98939). */}
				<div className="mt-auto flex w-full flex-col gap-2 pt-8">
					{/* `small`, like the compose sheets: the DS ModalFooter clones its buttons to that size, and the
					    design draws one button height across the flow (Janice, 2026-09-18). */}
					<Button variant="tertiary" size="small" className="w-full" onClick={flow.actions.finish}>
						{translate('corgi.contact_decision_maker.success.back_cta')}
					</Button>
					{/* Waits for the share links rather than opening a sheet with nothing in it. */}
					<Button
						variant="primary"
						size="small"
						className="w-full"
						loading={isShareLoading}
						onClick={flow.actions.openShare}
					>
						{translate('corgi.contact_decision_maker.success.share_cta')}
					</Button>
				</div>
			</div>
		</dialog>,
		document.body,
	);
};

/**
 * The design's `timeline4` promise card, all four rows. Row 2 renders for every count, "joins 0
 * others" included — hiding it hides its icon from the card, and the stats resolver is a stub
 * returning 0 until CHANGE-97530.
 */
const PromiseCard: React.FC<PromiseCardProps> = ({ decisionMakerName, otherSupporterCount }) => {
	const { localizeNumber } = useI18n();

	return (
		<ol
			className="border-stroke-weaker rounded-medium relative mt-6 flex w-full list-none flex-col gap-4 border border-solid p-4 text-left"
			data-testid="contact-decision-maker-success-promises"
		>
			{/* The rail runs behind the markers, from the first marker's centre to the last's. */}
			<span aria-hidden className="bg-stroke-weaker absolute top-7 bottom-7 left-7 -ml-px w-0.5" />
			<PromiseRow tone="done" icon={<CheckIcon size="x-small" className="text-icon-inverse-strong" />}>
				<Translate value="corgi.contact_decision_maker.success.promise_received" />
			</PromiseRow>
			{otherSupporterCount !== undefined && (
				// Intl output must not differ between SSR and hydration, same as the sub-line above.
				<ClientRender>
					<PromiseRow tone="next" icon={<GroupIcon size="x-small" className="text-icon-information" />}>
						<TranslatePlural
							value="corgi.contact_decision_maker.success.promise_joins"
							count={otherSupporterCount}
							replacements={{ otherCount: localizeNumber(otherSupporterCount) }}
						/>
					</PromiseRow>
				</ClientRender>
			)}
			<PromiseRow tone="next" icon={<SendIcon size="x-small" className="text-icon-information" />}>
				<Translate
					value="corgi.contact_decision_maker.success.promise_delivery"
					replacements={{ decision_maker_name: decisionMakerName }}
				/>
			</PromiseRow>
			<PromiseRow tone="next" icon={<EmailIcon size="x-small" className="text-icon-information" />}>
				<Translate
					value="corgi.contact_decision_maker.success.promise_reply"
					replacements={{ decision_maker_name: decisionMakerName }}
				/>
			</PromiseRow>
		</ol>
	);
};

type PromiseCardProps = {
	decisionMakerName: string;
	/** Everyone but the viewer; `undefined` while the count is loading. */
	otherSupporterCount: number | undefined;
};

/**
 * One step on the promise timeline. The marker is a 24px circle; the `done` tone is the design's
 * filled green "received" marker, `next` the tinted markers for what Change.org does from here.
 */
const PromiseRow: React.FC<PromiseRowProps> = ({ tone, icon, children }) => (
	<li className="flex items-center gap-3">
		{/* The ring masks the rail where it passes behind the marker. */}
		<span
			aria-hidden
			className={cn(
				'ring-background-base relative flex size-6 shrink-0 items-center justify-center rounded-full ring-4',
				tone === 'done' ? 'bg-fill-success-strong' : 'bg-fill-information-weaker',
			)}
		>
			{icon}
		</span>
		<Text as="span" className="text-size-body-small text-typography-strong">
			{children}
		</Text>
	</li>
);

type PromiseRowProps = {
	tone: 'done' | 'next';
	icon: React.ReactNode;
	children: React.ReactNode;
};
